import { useCallback } from 'react';
import type { Nullable } from 'corgi-types';

import { dispatchTrackingEventGA4 } from '../../../../tracking/dispatchTrackingEventGA4';
import type { ProductSuggestionItem, SuggestionItem } from '../../types';
import { trackSuggestionClick } from '../../utils/tracking';
import type { UseSuggestionsProps } from './types';

interface UseSuggestionsHandlersOptions extends Pick<
  UseSuggestionsProps,
  'isMobile' | 'inputValue' | 'unlockSearchAndClearInput' | 'setIsBodyOverlayVisible'
> {
  suggestions: SuggestionItem[];
  listItems: string[];
  clear: () => void;
}

interface GetExitHandlerOptions extends Pick<
  UseSuggestionsHandlersOptions,
  'setIsBodyOverlayVisible' | 'unlockSearchAndClearInput'
> {
  clearSuggestions: () => void;
}

type SuggestionsSelectHandler = (item: Nullable<SuggestionItem>) => void;

interface SelectHandlerOptions {
  inputValue?: string;
  listItems: string[];
  isRecentSearch?: boolean;
}

export const getSelectHandler =
  ({ inputValue, listItems, isRecentSearch }: SelectHandlerOptions) =>
  (selectedItem: Nullable<SuggestionItem | ProductSuggestionItem>) => {
    if (selectedItem) {
      if ('urlEncoded' in selectedItem) {
        trackSuggestionClick({
          label: selectedItem.name,
          inputValue,
          position: listItems.indexOf(selectedItem.name),
          length: listItems.length,
          isRecentSearch,
        });
        window.location.href = selectedItem.urlEncoded;
      }

      if ('url' in selectedItem) {
        dispatchTrackingEventGA4(selectedItem.trackingData);
        window.location.href = selectedItem.url;
      }
    }
  };

const useExitHandler = ({
  clearSuggestions,
  setIsBodyOverlayVisible,
  unlockSearchAndClearInput,
}: GetExitHandlerOptions) =>
  useCallback(
    (shouldClearInput = false) => {
      if (shouldClearInput) {
        unlockSearchAndClearInput();
      }
      clearSuggestions();
      setIsBodyOverlayVisible(false);
    },
    [unlockSearchAndClearInput, clearSuggestions, setIsBodyOverlayVisible]
  );

export const useSuggestionsHandlers = ({
  isMobile,
  clear,
  unlockSearchAndClearInput,
  setIsBodyOverlayVisible,
  suggestions,
  ...options
}: UseSuggestionsHandlersOptions) => {
  const exit = useExitHandler({
    clearSuggestions: clear,
    setIsBodyOverlayVisible,
    unlockSearchAndClearInput,
  });
  const handleSelect = useCallback<SuggestionsSelectHandler>(getSelectHandler(options), [options]);

  return {
    clear,
    exit,
    handleSelect,
  };
};
